import React from 'react';
import { TriangleAlert } from 'lucide-react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import type { WasteRow } from '../hooks/useWasteManagement';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  rows: WasteRow[];
  submitting: boolean;
  roastTypeLabelMap: Record<string, string>;
}

export const WasteAdjustConfirmModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  rows,
  submitting,
  roastTypeLabelMap,
}) => {
  const { t } = useLanguage();
  const adjustedKeepRows = rows.filter(
    (row) => row.action === 'keep' && Math.abs(Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0)) > 0.0005
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('workOrders.waste.confirmManualTitle')} maxWidth="md">
      <div className="space-y-4" data-test-id="waste-management-adjust-confirm-modal">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-700">
            <TriangleAlert className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <div className="text-sm font-semibold text-gray-900">{t('workOrders.waste.confirmManualHeading')}</div>
            <div className="text-sm text-gray-600">{t('workOrders.waste.confirmManualDescription')}</div>
          </div>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="space-y-2">
            {adjustedKeepRows.map((row) => {
              const delta = Number(row.remainingKg || 0) - Number(row.systemRemainingKg || 0);
              return (
                <div
                  key={`adjust-confirm-${row.itemId}-${row.roastType}`}
                  className="flex items-start justify-between gap-3 text-sm text-amber-900"
                  data-test-id={`waste-management-adjust-confirm-row-${row.itemId}-${row.roastType}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-amber-950">
                      {row.itemName}
                      {row.itemCode ? ` (${row.itemCode})` : ''}
                    </div>
                    <div className="text-xs text-amber-800">{roastTypeLabelMap[row.roastType] || row.roastType}</div>
                  </div>
                  <div className="flex-none text-right">
                    <div className="font-semibold">
                      {Number(row.systemRemainingKg || 0).toFixed(3)} → {Number(row.remainingKg || 0).toFixed(3)}
                    </div>
                    <div className="text-xs text-amber-800">
                      Δ {delta >= 0 ? '+' : ''}
                      {delta.toFixed(3)} kg
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            data-test-id="waste-management-adjust-confirm-cancel"
          >
            {t('common.actions.back', undefined, 'Back')}
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={submitting}
            className="inline-flex items-center rounded-md border border-transparent bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            data-test-id="waste-management-adjust-confirm-submit"
          >
            {submitting ? t('workOrders.waste.processing') : t('workOrders.waste.actions.confirmAdjust')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
